import { useCallback, useState } from 'react';
import { CardSize } from 'components/CardSizeSelector/CardSizeSelector';

/** localStorage keys are per surface so the offers list and builder remember independently */
export type CardSizeStorageKey =
  | 'tradeOffers.cardSize'
  | 'offerBuilder.cardSize'
  | 'search.cardSize';

const isCardSize = (value: string | null): value is CardSize =>
  value === 'S' || value === 'M' || value === 'L';

/**
 * Persisted card-art size, defaulting to M (design-pass decision 3).
 *
 * `allowed` narrows what a surface will accept: Search offers only M/L, so an 'S'
 * left in storage by another surface would otherwise select nothing.
 */
export const useCardSizePreference = (
  storageKey: CardSizeStorageKey,
  allowed?: CardSize[]
): [CardSize, (size: CardSize) => void] => {
  const [size, setSize] = useState<CardSize>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!isCardSize(stored)) return 'M';
      return !allowed || allowed.includes(stored) ? stored : 'M';
    } catch {
      return 'M';
    }
  });

  const update = useCallback(
    (next: CardSize) => {
      setSize(next);
      try {
        localStorage.setItem(storageKey, next);
      } catch {
        // localStorage unavailable — keep the in-memory value
      }
    },
    [storageKey]
  );

  return [size, update];
};

export default useCardSizePreference;
